import { useCallback, useRef } from 'react';

import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsUserUserDetailModel } from 'src/api/generated';
import { MobileDialog } from 'src/components/MobileDialog/MobileDialog';
import ScrollGradient from 'src/components/ScrollGradient';
import { useTheme } from 'src/contexts/ThemeContext';
import { useDeleteUser, useResetUserPassword } from 'src/hooks/api/useUsersEndpoint';
import { useHandleLongPress } from 'src/hooks/useLongPress';

import { ResetPasswordDescription, ResetPasswordWrapper } from '../../UsersTable.styled';
import {
  ChangePasswordButton,
  DeleteMobileButton,
  EditButton,
  MobileEmail,
  MobileRow,
  RowActions,
  RowActionsWrapper,
  Wrapper,
} from './UsersMobileRow.styled';

interface UsersMobileRowProps {
  users: FanguardApiContractsUserUserDetailModel[];
  user: FanguardApiContractsUserUserDetailModel;
  activeUsername: string | null;
  showResetPassword: boolean;
  setActiveUsername: (username: string | null) => void;
  onEditUser: (username: string) => void;
  onAfterDelete: () => void;
  isAdminUser?: boolean;
}

const UsersMobileRow = (props: UsersMobileRowProps) => {
  const {
    user,
    activeUsername,
    showResetPassword,
    setActiveUsername,
    onEditUser,
    onAfterDelete,
    isAdminUser = false,
  } = props;
  const rowRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { mutateAsync } = useDeleteUser();
  const { mutateAsync: resetUserPassword } = useResetUserPassword();
  const { combinedTheme } = useTheme();

  const { handlers } = useHandleLongPress({
    handleClickOutside: () => {
      setTimeout(() => setActiveUsername(null), 100);
    },
    handleSetLongPress: (context) => {
      if (context) {
        setActiveUsername(context);
      }
    },
    handleLongPressStart: () => {
      setActiveUsername(null);
    },
    ref: rowRef,
  });

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (activeUsername === user.username) {
      e.stopPropagation();
      e.preventDefault();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    return MobileDialog({
      title: `${t('general.delete')} ${t('users.user')}?`,
      description: t('general.cannotBeUndone'),
      confirmText: t('general.ok').toUpperCase(),
      cancelText: t('general.cancel'),
      onConfirm: async () => {
        if (user.username) {
          await mutateAsync(user.username);
          onAfterDelete();
        }
      },
      theme: combinedTheme,
    });
  };

  const handleClickChangePassword = (e: React.MouseEvent<HTMLElement>) => {
    MobileDialog({
      title: `${t('users.table.resetPassword')}?`,
      description: (
        <ResetPasswordWrapper>
          <ResetPasswordDescription $isDesktop={false}>
            {t('users.table.resetPasswordTitle', { username: user.username })}
          </ResetPasswordDescription>
          <ResetPasswordDescription $isDesktop={false}>
            {t('users.table.resetPasswordDescription', { email: user.email })}
          </ResetPasswordDescription>
        </ResetPasswordWrapper>
      ),
      onConfirm: () => handleResetUserPassword(e),
      theme: combinedTheme,
      confirmText: t('general.ok').toUpperCase(),
      cancelText: t('general.cancel'),
    });
  };
  const handleResetUserPassword = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    if (user.username) {
      await resetUserPassword(user.username);
    }
  };

  const handleClickEditButton = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }

      if (user.username) {
        onEditUser(user.username);
      }
    },
    [onEditUser, user.username],
  );

  console.log(activeUsername, 'activeUsername', user.username, 'user.username');
  console.log(activeUsername === user.username, 'activeUsername === user.username');

  return (
    <ScrollGradient>
      <Wrapper>
        <MobileRow
          {...handlers(user.username)}
          className={activeUsername === user.username ? 'active' : ''}
          onClick={handleRowClick}
          data-active={activeUsername === user.username ? 'true' : 'false'}
        >
          <Flex ref={rowRef}>
            <Flex vertical>
              <div>
                {user.firstName} {user.lastName}
              </div>
              <MobileEmail>{user.email}</MobileEmail>
            </Flex>
          </Flex>
        </MobileRow>

        {activeUsername === user.username || (activeUsername === user.username && showResetPassword && isAdminUser) ? (
          <RowActionsWrapper ref={actionsRef}>
            <RowActions>
              <ChangePasswordButton
                data-ignore-outside
                aria-label="change password"
                onClick={handleClickChangePassword}
              />
              <EditButton data-ignore-outside onClick={handleClickEditButton} />
              <DeleteMobileButton data-ignore-outside onClick={handleDeleteClick} />
            </RowActions>
          </RowActionsWrapper>
        ) : null}
      </Wrapper>
    </ScrollGradient>
  );
};

export default UsersMobileRow;
